import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, IconButton, IconSizes, Paper, Skeleton, Typography } from 'libs/shared/ui';

import { styled } from '@mui/material';

import { selectTagColorMode, selectTagStyling, selectTagVariant } from 'Config/configSlice';
import { CourseChip, getTagColor } from 'Courses/components/shared';
import { useAppSelector } from 'Store';

import { AddIcon, RemoveIcon } from '@libs/shared/ui/icons';
import { Colors } from '@themes/colors';
import { Spacing } from '@themes/spacing';

import type { CourseTagWithTranslation, TagTypeType } from '../types';
import { TagType } from '../types';

const TagOptionsContainer = styled(Paper)`
  border: 1px solid ${Colors.lightGrey};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const TagOptionItem = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${Spacing.xsmall};
  padding: 6px 12px;
  border-bottom: 1px solid ${Colors.lightGrey};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${Colors.lightGrey};
  }
`;

const ClickableTagChip = styled(CourseChip)`
  cursor: pointer;
  pointer-events: auto;
`;

const StyledIncludeButton = styled(IconButton)`
  color: ${Colors.green} !important;

  &:hover {
    background-color: ${Colors.green}10 !important;
  }

  &:focus {
    outline: 2px solid ${Colors.green} !important;
    outline-offset: 2px;
  }
`;

const StyledExcludeButton = styled(IconButton)`
  color: ${Colors.red} !important;

  &:hover {
    background-color: ${Colors.red}10 !important;
  }

  &:focus {
    outline: 2px solid ${Colors.red} !important;
    outline-offset: 2px;
  }
`;

type TagOptionsProps = Readonly<{
  availableTags: CourseTagWithTranslation[];
  selectedTagsCount: number;
  maxTags: number;
  isLoading: boolean;
  onTagAdd: (tag: CourseTagWithTranslation, type: TagTypeType) => void;
  getTagTranslation: (tag: CourseTagWithTranslation) => string;
}>;

export const TagOptions = ({
  availableTags,
  selectedTagsCount,
  maxTags,
  isLoading,
  onTagAdd,
  getTagTranslation
}: TagOptionsProps) => {
  const { t } = useTranslation();
  const tagVariant = useAppSelector(selectTagVariant);
  const tagColorMode = useAppSelector(selectTagColorMode);
  const tagStyling = useAppSelector(selectTagStyling);

  if (isLoading) {
    return (
      <TagOptionsContainer>
        <Box sx={{ p: 1 }} role="status" aria-live="polite">
          <Typography sx={{ sr: 'only' }}>
            {t('coursesPage.loadingTags', 'Loading available tags...')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Skeleton height={28} />
            <Skeleton height={28} />
            <Skeleton height={28} />
          </Box>
        </Box>
      </TagOptionsContainer>
    );
  }

  if (availableTags.length === 0) {
    return (
      <TagOptionsContainer>
        <Box sx={{ p: 1, textAlign: 'center' }} role="status">
          <Typography variant="body2" sx={{ color: Colors.darkGrey, fontSize: '12px' }}>
            {selectedTagsCount >= maxTags
              ? t('coursesPage.maxTagsReached', `Maximum ${maxTags} tags allowed`)
              : t('coursesPage.noTagsAvailable', 'No more tags available')}
          </Typography>
        </Box>
      </TagOptionsContainer>
    );
  }

  return (
    <TagOptionsContainer
      id="search-tags-listbox"
      role="listbox"
      aria-label={t('coursesPage.availableTagsListbox', 'Available tags to add')}
    >
      {availableTags.map((tag, index) => {
        if (!tag?.value) {
          return null;
        }

        const tagColor = getTagColor(tag.value, tagVariant, tagStyling.uniformColor, tagColorMode);
        const tagLabel = getTagTranslation(tag);
        const isDisabled = selectedTagsCount >= maxTags;

        const handleTagClick = () => {
          if (!isDisabled) {
            onTagAdd(tag, TagType.Include);
          }
        };

        return (
          <TagOptionItem
            key={tag.value}
            role="option"
            aria-selected="false"
            aria-posinset={index + 1}
            aria-setsize={availableTags.length}
          >
            <StyledIncludeButton
              size="small"
              onClick={() => onTagAdd(tag, TagType.Include)}
              aria-label={t('coursesPage.includeTagWithName', { tagName: tagLabel })}
              disabled={isDisabled}
              data-testid="tag-include-button"
            >
              <AddIcon fontSize={IconSizes.small} aria-hidden="true" />
            </StyledIncludeButton>
            <ClickableTagChip
              backgroundColor={tagColor}
              variant={tagVariant}
              uniformColor={tagStyling.uniformColor}
              borderSize={tagStyling.borderSize}
              fontSize={tagStyling.fontSize}
              colorMode={tagColorMode}
              onClick={handleTagClick}
              aria-label={t('coursesPage.includeTagWithName', { tagName: tagLabel })}
              style={{ opacity: isDisabled ? 0.6 : 1 }}
            >
              {tagLabel}
            </ClickableTagChip>
            <StyledExcludeButton
              size="small"
              onClick={() => onTagAdd(tag, TagType.Exclude)}
              aria-label={t('coursesPage.excludeTagWithName', { tagName: tagLabel })}
              disabled={isDisabled}
              data-testid="tag-exclude-button"
            >
              <RemoveIcon fontSize={IconSizes.small} aria-hidden="true" />
            </StyledExcludeButton>
          </TagOptionItem>
        );
      })}
    </TagOptionsContainer>
  );
};
